import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Briefcase,
    Check,
    ChevronDown,
    ChevronUp,
    Circle,
    CircleDashed,
    File,
    FileText,
    HelpCircle,
    LogOut,
    Mail,
    Network,
    Paperclip,
    Plus,
    Quote,
    ShieldCheck,
    Trash2,
    UserCircle2,
    X,
} from "lucide-react";
import { clientApi, useClientAuth } from "@/lib/clientAuth";
import { ClientOnboardingTour, ProjectSynthesisCard, ClientTermsConsent } from "./ClientOnboarding";
import ClientDossierModules from "./ClientDossierModules";
import ClientCommitteeSimulator from "./ClientCommitteeSimulator";

/* eslint-disable react/no-unescaped-entities */

function fmtSize(n) {
    if (!n) return "—";
    const k = 1024,
        s = ["o", "Ko", "Mo", "Go"];
    const i = Math.floor(Math.log(n) / Math.log(k));
    return `${(n / Math.pow(k, i)).toFixed(1)} ${s[i]}`;
}

export default function ClientEspace() {
    const { client, ready, logout } = useClientAuth();
    const navigate = useNavigate();
    const [dossier, setDossier] = useState(null);
    const [docs, setDocs] = useState([]);
    const [reports, setReports] = useState([]);
    const [contribs, setContribs] = useState([]);
    const [partnerInsight, setPartnerInsight] = useState(null);
    const [modalStepId, setModalStepId] = useState(null);
    const [err, setErr] = useState("");
    const [me, setMe] = useState(null);
    const [synthesis, setSynthesis] = useState(null);
    const [tourOpen, setTourOpen] = useState(false);
    const [termsOpen, setTermsOpen] = useState(false);

    useEffect(() => {
        if (ready && !client) navigate("/client/login");
    }, [ready, client, navigate]);

    const reloadContribs = () =>
        clientApi
            .get("/dossier/contributions")
            .then((r) => setContribs(r.data))
            .catch(() => {});

    useEffect(() => {
        if (!client) return;
        Promise.all([
            clientApi.get("/dossier").then((r) => r.data),
            clientApi.get("/dossier/documents").then((r) => r.data),
            clientApi.get("/dossier/reports").then((r) => r.data),
            clientApi.get("/dossier/contributions").then((r) => r.data),
            clientApi
                .get("/dossier/partner-insight")
                .then((r) => r.data)
                .catch(() => null),
            clientApi.get("/auth/me").then((r) => r.data).catch(() => null),
        ])
            .then(([d, dd, rr, cc, pi, m]) => {
                setDossier(d);
                setDocs(dd);
                setReports(rr);
                setContribs(cc);
                setPartnerInsight(pi);
                setMe(m);
                setSynthesis(
                    d?.project_synthesis || m?.project_synthesis || null,
                );
                if (m && !m.tour_completed_at) {
                    setTourOpen(true);
                }
                // Terms acceptance: show if not yet accepted AND not
                // dismissed in current session.
                let dismissed = false;
                try {
                    dismissed = sessionStorage.getItem("xv_terms_dismissed") === "1";
                } catch {
                    /* SSR safe */
                }
                if (m && !m.terms_accepted_at && !dismissed) {
                    setTermsOpen(true);
                }
            })
            .catch((e) =>
                setErr(e?.response?.data?.detail || "Erreur de chargement")
            );
    }, [client]);

    const handleLogout = () => {
        logout();
        navigate("/client/login");
    };

    const download = async (doc) => {
        const res = await clientApi.get(`/dossier/documents/${doc.id}`, {
            responseType: "blob",
        });
        const url = URL.createObjectURL(res.data);
        const a = document.createElement("a");
        a.href = url;
        a.download = doc.filename;
        a.click();
        URL.revokeObjectURL(url);
    };

    if (!ready || !client) return <div className="min-h-screen bg-[#070d18]" />;
    if (err)
        return (
            <div className="p-16 text-[#e89090] text-sm bg-[#070d18] min-h-screen">
                {err}
            </div>
        );
    if (!dossier)
        return (
            <div className="p-16 text-[#f4ecd8]/40 text-sm bg-[#070d18] min-h-screen">
                Chargement de votre dossier…
            </div>
        );

    const total = dossier.timeline.length;
    // A step is "achieved" if state=done OR a validated report exists
    // (the backend only exposes `report` when validated).
    const isAchieved = (s) => s.state === "done" || !!s.report;
    const done = dossier.timeline.filter(isAchieved).length;
    const pct = Math.round((100 * done) / Math.max(total, 1));
    const inProgressIdx = dossier.timeline.findIndex(
        (s) => s.state === "in_progress" && !isAchieved(s)
    );
    const nextIdx = inProgressIdx >= 0
        ? inProgressIdx
        : dossier.timeline.findIndex((s) => !isAchieved(s));
    const nextStep = nextIdx >= 0 ? dossier.timeline[nextIdx] : null;

    return (
        <div
            data-testid="client-espace"
            className="min-h-screen bg-[#0b1220] text-[#f4ecd8] font-sans-modern relative"
        >
            {tourOpen && (
                <ClientOnboardingTour onClose={() => setTourOpen(false)} />
            )}
            {termsOpen && (
                <ClientTermsConsent
                    onClose={() => setTermsOpen(false)}
                />
            )}
            <div
                aria-hidden="true"
                className="pointer-events-none fixed inset-0 opacity-[0.04]"
                style={{
                    backgroundImage:
                        "radial-gradient(circle at 8% 8%, rgba(212,175,55,0.55) 0px, transparent 36%), radial-gradient(circle at 92% 88%, rgba(212,175,55,0.4) 0px, transparent 42%)",
                }}
            />

            {/* HEADER BAR */}
            <header className="relative border-b border-white/[0.06] px-8 lg:px-16 py-6 flex items-center justify-between gap-6">
                <div className="flex items-center gap-3">
                    <img src="/brand/icon-gold.png" alt="" className="h-7 w-auto opacity-90" />
                    <div className="flex flex-col leading-none">
                        <span className="font-serif text-[14px] tracking-[0.3em] uppercase">Espace client</span>
                        <span className="mt-1.5 text-[8.5px] tracking-[0.42em] uppercase text-[#d4af37]/80">
                            XV Capital Partners
                        </span>
                    </div>
                </div>
                <div className="flex items-center gap-6">
                    <div className="text-right hidden sm:block">
                        <div className="font-serif text-[14px] text-[#f4ecd8]">{client.name || client.email}</div>
                        <div className="text-[10px] tracking-[0.28em] uppercase text-[#d4af37]/60 mt-1">Accès confidentiel</div>
                    </div>
                    <button
                        type="button"
                        data-testid="client-logout"
                        onClick={handleLogout}
                        className="inline-flex items-center gap-2 px-4 py-2.5 text-[10px] tracking-[0.28em] uppercase text-[#f4ecd8]/60 hover:text-[#f4ecd8] transition-colors duration-500 border border-white/15 hover:border-white/30"
                    >
                        <LogOut strokeWidth={1.2} size={12} />
                        Quitter
                    </button>
                </div>
            </header>

            <main className="relative px-8 lg:px-16 py-14">
                {/* Hero */}
                <section className="mb-16">
                    <div className="overline text-[#d4af37]/70 mb-4">Suivi de dossier</div>
                    <h1
                        data-testid="client-dossier-name"
                        className="font-serif text-[26px] sm:text-[34px] md:text-[44px] lg:text-[52px] leading-[1.08]"
                        style={{ fontWeight: 300 }}
                    >
                        {dossier.name}
                    </h1>
                    <div className="mt-6 flex flex-wrap gap-x-8 gap-y-3 text-[13px] text-[#f4ecd8]/65">
                        {dossier.sector && (
                            <span>
                                <span className="text-[10px] tracking-[0.28em] uppercase text-[#f4ecd8]/40 mr-2">Secteur</span>
                                <span className="font-serif">{dossier.sector}</span>
                            </span>
                        )}
                        {dossier.geography && (
                            <span>
                                <span className="text-[10px] tracking-[0.28em] uppercase text-[#f4ecd8]/40 mr-2">Géographie</span>
                                <span className="font-serif">{dossier.geography}</span>
                            </span>
                        )}
                        {dossier.operation_type && (
                            <span>
                                <span className="text-[10px] tracking-[0.28em] uppercase text-[#f4ecd8]/40 mr-2">Opération</span>
                                <span className="font-serif">{dossier.operation_type}</span>
                            </span>
                        )}
                    </div>
                </section>

                {/* SYNTHESIS — client's own boussole (one-shot lockable) */}
                <ProjectSynthesisCard
                    synthesis={synthesis}
                    onSaved={(s) => setSynthesis(s)}
                />

                {/* SUMMARY + INTERLOCUTOR */}
                <section
                    data-testid="client-summary-block"
                    className="grid lg:grid-cols-12 gap-10 mb-16"
                >
                    <div className="lg:col-span-8 bg-[#070d18] border border-[#d4af37]/15 p-10">
                        <div className="overline text-[#d4af37]/80 mb-5">
                            Résumé de la mission
                        </div>
                        {dossier.summary ? (
                            <p
                                data-testid="client-summary-text"
                                className="font-serif text-[15.5px] sm:text-[17px] text-[#f4ecd8]/85 leading-[1.95] whitespace-pre-wrap"
                            >
                                {dossier.summary}
                            </p>
                        ) : (
                            <p className="text-[#f4ecd8]/40 text-[14px] italic">
                                Le résumé détaillé de votre mission sera mis à
                                votre disposition prochainement par votre
                                interlocuteur.
                            </p>
                        )}

                        <div className="mt-10 grid sm:grid-cols-2 gap-x-10 gap-y-6 border-t border-white/[0.06] pt-8">
                            {dossier.ticket && (
                                <div data-testid="client-ticket">
                                    <div className="text-[10px] tracking-[0.32em] uppercase text-[#d4af37]/70 mb-2">
                                        Enveloppe envisagée
                                    </div>
                                    <div
                                        className="font-serif text-[26px] sm:text-[28px] leading-none text-[#f4ecd8]"
                                        style={{ fontWeight: 300 }}
                                    >
                                        {dossier.ticket}
                                    </div>
                                </div>
                            )}
                            {dossier.maturity && (
                                <div>
                                    <div className="text-[10px] tracking-[0.32em] uppercase text-[#d4af37]/70 mb-2">
                                        Maturité de l'opération
                                    </div>
                                    <div className="font-serif text-[18px] text-[#f4ecd8]/90 leading-snug">
                                        {dossier.maturity}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    <aside
                        data-testid="client-interlocutor"
                        className="lg:col-span-4 bg-[#070d18] border border-[#d4af37]/15 p-10 flex flex-col"
                    >
                        <div className="overline text-[#d4af37]/80 mb-5">
                            Votre interlocuteur
                        </div>
                        <div className="flex items-start gap-4">
                            <span className="w-14 h-14 rounded-full border border-[#d4af37]/40 overflow-hidden shrink-0 bg-[#0b1220]">
                                <img
                                    src="/brand/joel-kotho-avatar.jpg"
                                    alt="Joel Kotho"
                                    className="w-full h-full object-cover"
                                />
                            </span>
                            <div>
                                <div
                                    className="font-serif text-[22px] text-[#f4ecd8] leading-tight"
                                    style={{ fontWeight: 400 }}
                                >
                                    {dossier.interlocutor?.name || "Joel Kotho"}
                                </div>
                                <div className="mt-1.5 text-[11px] tracking-[0.22em] uppercase text-[#d4af37]/70">
                                    {dossier.interlocutor?.role ||
                                        "Fondateur · Managing Partner"}
                                </div>
                            </div>
                        </div>
                        <p className="mt-6 text-[12.5px] text-[#f4ecd8]/55 leading-[1.85] font-light italic">
                            Pour toute question relative à votre dossier,
                            n'hésitez pas à contacter directement votre
                            interlocuteur dédié.
                        </p>
                        {dossier.interlocutor?.email && (
                            <a
                                href={`mailto:${dossier.interlocutor.email}?subject=Suivi%20de%20dossier%20%E2%80%94%20${encodeURIComponent(dossier.name)}`}
                                data-testid="client-interlocutor-mailto"
                                className="mt-auto pt-8 inline-flex items-center gap-3 text-[10px] tracking-[0.3em] uppercase text-[#d4af37]/85 border-b border-[#d4af37]/30 hover:border-[#d4af37] hover:text-[#d4af37] pb-1 w-fit transition-colors duration-500"
                            >
                                <Mail strokeWidth={1.2} size={12} />
                                Lui écrire en confidentiel
                            </a>
                        )}
                    </aside>
                </section>

                {/* PROGRESS + NEXT */}
                <section className="grid lg:grid-cols-12 gap-10 mb-16">
                    <div className="lg:col-span-7 bg-white/[0.02] border border-white/[0.06] p-10">
                        <div className="flex items-baseline justify-between mb-6">
                            <div>
                                <div className="overline text-[#d4af37]/70 mb-2">Avancement global</div>
                                <div className="font-serif text-[36px] sm:text-[44px] leading-none" style={{ fontWeight: 300 }}>
                                    {pct}<span className="text-[#d4af37] text-[28px] ml-1">%</span>
                                </div>
                            </div>
                            <div className="text-[10px] tracking-[0.28em] uppercase text-[#d4af37]/80">
                                Statut · {dossier.status_label}
                            </div>
                        </div>
                        <div className="h-[2px] bg-white/10 relative overflow-hidden">
                            <div
                                className="absolute inset-y-0 left-0 bg-[#d4af37] transition-all duration-1000"
                                style={{ width: `${pct}%` }}
                            />
                        </div>
                        <p className="mt-6 text-[12px] text-[#f4ecd8]/45">
                            {done} étape{done > 1 ? "s" : ""} terminée{done > 1 ? "s" : ""} sur {total}
                        </p>
                    </div>
                    <div className="lg:col-span-5 bg-white/[0.02] border border-white/[0.06] p-10">
                        <div className="overline text-[#d4af37]/70 mb-2">Prochaine action</div>
                        <div className="font-serif text-[22px] md:text-[26px] leading-tight mt-3" style={{ fontWeight: 400 }}>
                            {nextStep ? nextStep.label : "Toutes les étapes sont terminées."}
                        </div>
                        {nextStep && (
                            <>
                                <p className="mt-5 text-[13px] text-[#f4ecd8]/70 leading-[1.85]">
                                    Finalisation des échanges préliminaires
                                    avec les partenaires stratégiques
                                    sélectionnés.
                                </p>
                                <div className="mt-7 pt-5 border-t border-white/[0.06] grid grid-cols-2 gap-x-6 gap-y-3">
                                    <div>
                                        <div className="text-[10px] tracking-[0.28em] uppercase text-[#f4ecd8]/40">Responsable</div>
                                        <div className="mt-1.5 text-[13px] text-[#f4ecd8]/85 font-serif">
                                            {dossier.interlocutor?.name || "Joel Kotho"}
                                        </div>
                                    </div>
                                    <div>
                                        <div className="text-[10px] tracking-[0.28em] uppercase text-[#f4ecd8]/40">Échéance estimée</div>
                                        <div className="mt-1.5 text-[13px] text-[#f4ecd8]/85 font-serif italic">
                                            À confirmer
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </section>

                {/* ACTIVITÉ RÉCENTE — generated from validated reports + done steps */}
                <RecentActivityBlock dossier={dossier} />

                {/* PARTNER INSIGHT — sanitised count + average compatibility */}
                {partnerInsight?.available && (
                    <PartnerInsightCard insight={partnerInsight} />
                )}

                {/* TIMELINE */}
                <section className="mb-16">
                    <div className="overline text-[#d4af37]/70 mb-3">Étapes du dossier</div>
                    <h2 className="font-serif text-[24px] mb-10" style={{ fontWeight: 400 }}>
                        Avancement détaillé
                    </h2>
                    <ul className="space-y-1">
                        {dossier.timeline.map((s, i) => (
                            <ClientStepRow
                                key={s.id || i}
                                step={s}
                                contributions={contribs.filter(
                                    (c) => c.step_id === s.id
                                )}
                                onAddInfo={() => setModalStepId(s.id)}
                            />
                        ))}
                    </ul>
                </section>

                {/* STRATEGIC SHAREABLES — expertises mobilisées, questions à l'étude, réflexions */}
                <ClientShareablesView dossier={dossier} />

                {/* INVESTMENT OFFICE MODULES — optional premium modules */}
                <ClientDossierModules />

                {/* COMMITTEE SIMULATOR — unlocks at "Discussions confidentielles" */}
                <ClientCommitteeSimulator />

                {/* JOURNAL STRATÉGIQUE PARTAGÉ */}
                <SharedJournal
                    dossier={dossier}
                    contributions={contribs}
                    reports={reports}
                    documents={docs}
                />

                {/* DOCS + REPORTS */}
                <section className="grid lg:grid-cols-2 gap-10 mb-16">
                    <div>
                        <div className="overline text-[#d4af37]/70 mb-3">Documents partagés</div>
                        <h2 className="font-serif text-[22px] mb-8" style={{ fontWeight: 400 }}>
                            Coffre-fort
                        </h2>
                        <div className="border-t border-white/[0.06]">
                            {docs.length === 0 ? (
                                <p className="text-[#f4ecd8]/40 text-sm py-6 italic">
                                    Aucun document n'a encore été partagé avec vous.
                                </p>
                            ) : (
                                docs.map((d) => (
                                    <button
                                        key={d.id}
                                        type="button"
                                        data-testid={`client-doc-${d.id}`}
                                        onClick={() => download(d)}
                                        className="w-full grid grid-cols-[auto_1fr_auto] items-center gap-4 py-4 border-b border-white/[0.06] hover:bg-white/[0.02] transition-colors duration-500 px-2 text-left"
                                    >
                                        <File strokeWidth={1} size={18} className="text-[#d4af37]/70" />
                                        <span className="font-serif text-[14px] text-[#f4ecd8] truncate">{d.filename}</span>
                                        <span className="text-[11px] text-[#f4ecd8]/40">{fmtSize(d.size)}</span>
                                    </button>
                                ))
                            )}
                        </div>
                    </div>
                    <div>
                        <div className="overline text-[#d4af37]/70 mb-3">Synthèses confidentielles</div>
                        <h2 className="font-serif text-[22px] mb-8" style={{ fontWeight: 400 }}>
                            Synthèses partagées
                        </h2>
                        <div className="border-t border-white/[0.06]">
                            {reports.length === 0 ? (
                                <p className="text-[#f4ecd8]/40 text-sm py-6 italic">
                                    Aucune synthèse n'a encore été partagée.
                                </p>
                            ) : (
                                reports.map((r) => (
                                    <div
                                        key={r.id}
                                        data-testid={`client-report-${r.id}`}
                                        className="py-5 border-b border-white/[0.06] px-2"
                                    >
                                        <div className="flex items-center gap-3 mb-3">
                                            <FileText strokeWidth={1} size={16} className="text-[#d4af37]/70" />
                                            <div className="font-serif text-[17px] text-[#f4ecd8]" style={{ fontWeight: 400 }}>
                                                {r.title}
                                            </div>
                                        </div>
                                        <div className="text-[11px] text-[#f4ecd8]/40 mb-3 tracking-wide">
                                            Validé le{" "}
                                            {r.validated_at
                                                ? new Date(r.validated_at).toLocaleDateString("fr-FR")
                                                : "—"}
                                        </div>
                                        <p className="text-[13px] text-[#f4ecd8]/75 leading-[1.85] font-light whitespace-pre-line">
                                            {r.body}
                                        </p>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </section>

                {/* FOOTER COMPLIANCE — confidentialité + protection légale */}
                <footer className="border-t border-white/[0.06] pt-8 space-y-5">
                    <div className="text-[10px] tracking-[0.32em] uppercase text-[#f4ecd8]/35 flex flex-wrap gap-3 items-center">
                        <ShieldCheck strokeWidth={1.2} size={12} />
                        Espace confidentiel · Accès strictement nominatif · Sessions horodatées
                    </div>
                    <p className="text-[11px] text-[#f4ecd8]/40 font-light leading-[1.85] max-w-3xl italic">
                        Les informations présentées dans cet espace sont
                        strictement confidentielles et ne constituent ni une
                        offre au public, ni une sollicitation d'investissement,
                        ni un conseil en investissement au sens de la
                        réglementation applicable.
                    </p>
                </footer>
            </main>

            {/* Modal contribution */}
            {modalStepId && (
                <ContributionModal
                    stepId={modalStepId}
                    stepLabel={
                        dossier.timeline.find((s) => s.id === modalStepId)?.label
                    }
                    onClose={() => setModalStepId(null)}
                    onSaved={() => {
                        reloadContribs();
                        setModalStepId(null);
                    }}
                />
            )}
        </div>
    );
}


function ClientStepRow({ step, contributions = [], onAddInfo }) {
    // Backward-compat: prefer the new reports[] array; fall back to the
    // legacy single report{} if needed.
    const reportsList = (() => {
        if (Array.isArray(step.reports) && step.reports.length > 0) {
            return step.reports;
        }
        if (step.report && (step.report.body || "").trim()) {
            return [step.report];
        }
        return [];
    })();
    const hasReport = reportsList.length > 0;
    // A validated CR ⇒ the step is effectively complete on the client side.
    const effectiveState =
        step.state === "done" || hasReport ? "done" : step.state;
    const Icon =
        effectiveState === "done"
            ? Check
            : effectiveState === "in_progress"
              ? Circle
              : CircleDashed;
    const hasContribs = contributions.length > 0;
    const expandable = hasReport || hasContribs;
    const [open, setOpen] = useState(false);

    return (
        <li
            data-testid={`client-step-${step.order}`}
            className="border-b border-white/[0.06]"
        >
            <div className="flex items-center gap-4 sm:gap-6 py-4 px-2">
                <span
                    className={`w-9 h-9 rounded-full flex items-center justify-center border shrink-0 ${
                        effectiveState === "done"
                            ? "border-[#d4af37] bg-[#d4af37]/10 text-[#d4af37]"
                            : effectiveState === "in_progress"
                              ? "border-[#d4af37]/70 text-[#d4af37]/90"
                              : "border-white/15 text-[#f4ecd8]/30"
                    }`}
                >
                    <Icon strokeWidth={1.4} size={14} />
                </span>
                <span className="text-[10px] tracking-[0.3em] text-[#d4af37]/60 w-8 shrink-0 hidden sm:inline">
                    {String(step.order).padStart(2, "0")}
                </span>
                <button
                    type="button"
                    onClick={() => expandable && setOpen((o) => !o)}
                    disabled={!expandable}
                    className={`flex-1 text-left min-w-0 ${
                        expandable ? "cursor-pointer" : "cursor-default"
                    }`}
                >
                    <div
                        className={`font-serif text-[16px] sm:text-[18px] md:text-[20px] leading-tight ${
                            effectiveState === "done"
                                ? "text-[#f4ecd8]"
                                : "text-[#f4ecd8]/80"
                        }`}
                    >
                        {step.label}
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1">
                        {hasReport && (
                            <span className="inline-flex items-center gap-2 text-[10px] tracking-[0.22em] uppercase text-[#d4af37]/80">
                                <FileText strokeWidth={1.3} size={10} />
                                {reportsList.length === 1
                                    ? "Synthèse confidentielle disponible"
                                    : `${reportsList.length} synthèses confidentielles`}
                            </span>
                        )}
                        {hasContribs && (
                            <span className="inline-flex items-center gap-2 text-[10px] tracking-[0.22em] uppercase text-[#d4af37]/65">
                                {contributions.length} contribution{contributions.length > 1 ? "s" : ""}
                            </span>
                        )}
                    </div>
                </button>
                <span className="text-[10px] tracking-[0.26em] uppercase text-[#f4ecd8]/40">
                    {effectiveState === "done"
                        ? "Terminé"
                        : effectiveState === "in_progress"
                          ? "En cours"
                          : "À venir"}
                </span>
                {expandable && (
                    <button
                        type="button"
                        data-testid={`client-step-cr-toggle-${step.order}`}
                        onClick={() => setOpen((o) => !o)}
                        className="text-[#d4af37]/70 hover:text-[#d4af37] transition-colors duration-500"
                        aria-label={open ? "Replier" : "Déplier"}
                    >
                        {open ? (
                            <ChevronUp strokeWidth={1.3} size={16} />
                        ) : (
                            <ChevronDown strokeWidth={1.3} size={16} />
                        )}
                    </button>
                )}
            </div>
            {expandable && open && (
                <div className="mx-2 mb-4 space-y-3">
                    {hasReport && (
                        <div
                            data-testid={`client-step-report-${step.order}`}
                            className="space-y-3"
                        >
                            {reportsList.map((rep, idx) => (
                                <article
                                    key={rep.id || idx}
                                    data-testid={`client-step-report-item-${step.order}-${idx}`}
                                    className="border-l-2 border-[#d4af37]/40 bg-[#070d18] px-5 sm:px-8 py-7"
                                >
                                    {rep.title && (
                                        <div
                                            className="font-serif text-[18px] sm:text-[20px] text-[#f4ecd8] mb-4 leading-tight"
                                            style={{ fontWeight: 400 }}
                                        >
                                            {rep.title}
                                        </div>
                                    )}
                                    <div className="font-serif text-[14.5px] sm:text-[15.5px] text-[#f4ecd8]/85 leading-[1.95] whitespace-pre-wrap">
                                        {rep.body}
                                    </div>
                                    {rep.updated_at && (
                                        <div className="mt-6 text-[10px] tracking-[0.26em] uppercase text-[#f4ecd8]/35">
                                            Dernière mise à jour ·{" "}
                                            {new Date(rep.updated_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" })}
                                        </div>
                                    )}
                                </article>
                            ))}
                        </div>
                    )}
                    {hasContribs && (
                        <div className="border-l-2 border-[#d4af37]/20 bg-[#070d18]/60 px-5 sm:px-8 py-6 space-y-4">
                            <div className="overline text-[#d4af37]/70 mb-1">
                                Vos contributions
                            </div>
                            {contributions.map((c) => (
                                <ContributionRow key={c.id} contribution={c} />
                            ))}
                        </div>
                    )}
                </div>
            )}
            {/* Add info button — always visible, below */}
            <div className="px-2 pb-4 -mt-1">
                <button
                    type="button"
                    data-testid={`client-step-add-info-${step.order}`}
                    onClick={onAddInfo}
                    className="inline-flex items-center gap-2 text-[10px] tracking-[0.26em] uppercase text-[#f4ecd8]/45 hover:text-[#d4af37] transition-colors duration-500"
                >
                    <Plus strokeWidth={1.3} size={11} />
                    Ajouter une information
                </button>
            </div>
        </li>
    );
}


function ContributionRow({ contribution: c }) {
    return (
        <div data-testid={`contribution-${c.id}`}>
            <div className="flex items-baseline gap-3 flex-wrap">
                <div className="font-serif text-[16px] text-[#f4ecd8]/95" style={{ fontWeight: 400 }}>
                    {c.title}
                </div>
                {c.confidential && (
                    <span className="text-[9px] tracking-[0.28em] uppercase text-[#d4af37]/70">
                        · XV uniquement
                    </span>
                )}
            </div>
            <div className="mt-1 text-[10px] tracking-[0.24em] uppercase text-[#f4ecd8]/35">
                {c.date ? new Date(c.date).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" }) : "—"}
                {c.author_name ? ` · ${c.author_name}` : ""}
            </div>
            {c.note && (
                <p className="mt-3 font-serif text-[13.5px] text-[#f4ecd8]/75 leading-[1.85] whitespace-pre-wrap">
                    {c.note}
                </p>
            )}
            {c.attachment?.filename && (
                <div className="mt-3 inline-flex items-center gap-2 text-[11px] text-[#d4af37]/80">
                    <Paperclip strokeWidth={1.3} size={11} />
                    {c.attachment.filename}
                </div>
            )}
        </div>
    );
}


function ContributionModal({ stepId, stepLabel, onClose, onSaved }) {
    const [title, setTitle] = useState("");
    const [note, setNote] = useState("");
    const [date, setDate] = useState(() => {
        const d = new Date();
        return d.toISOString().slice(0, 10);
    });
    const [confidential, setConfidential] = useState(false);
    const [file, setFile] = useState(null);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");

    const submit = async () => {
        setErr("");
        if (!title.trim()) {
            setErr("Le titre est obligatoire.");
            return;
        }
        setBusy(true);
        try {
            const { data: contrib } = await clientApi.post(
                "/dossier/contributions",
                {
                    title: title.trim(),
                    note: note.trim(),
                    date: date ? new Date(date).toISOString() : null,
                    step_id: stepId,
                    confidential,
                }
            );
            if (file) {
                const fd = new FormData();
                fd.append("file", file);
                await clientApi.post(
                    `/dossier/contributions/${contrib.id}/attachment`,
                    fd,
                    { headers: { "Content-Type": "multipart/form-data" } }
                );
            }
            onSaved();
        } catch (e) {
            setErr(e?.response?.data?.detail || "Erreur lors de l'envoi");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div
            data-testid="contribution-modal"
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
        >
            <button
                aria-label="Fermer"
                onClick={onClose}
                className="absolute inset-0 bg-[#070d18]/85 backdrop-blur-sm"
            />
            <div className="relative w-full max-w-[640px] bg-[#0b1220] border border-[#d4af37]/20 max-h-[90vh] overflow-y-auto">
                <div className="px-7 sm:px-10 py-6 border-b border-white/[0.06] flex items-start justify-between gap-4">
                    <div>
                        <div className="overline text-[#d4af37]/80 mb-2">
                            Nouvelle contribution
                        </div>
                        <div className="font-serif text-[20px] sm:text-[24px] text-[#f4ecd8]" style={{ fontWeight: 400 }}>
                            Étape · {stepLabel}
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="text-[#f4ecd8]/50 hover:text-[#f4ecd8] transition-colors"
                        aria-label="Fermer"
                    >
                        <X strokeWidth={1.3} size={20} />
                    </button>
                </div>

                <div className="px-7 sm:px-10 py-7 space-y-6">
                    <div>
                        <label className="block text-[10px] tracking-[0.28em] uppercase text-[#d4af37]/70 mb-2">
                            Titre <span className="text-[#e89090]/70">*</span>
                        </label>
                        <input
                            data-testid="contribution-title"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder="Information stratégique, contact partenaire…"
                            className="w-full bg-transparent border-b border-white/15 focus:border-[#d4af37] outline-none py-2.5 text-[15px] font-serif text-[#f4ecd8] placeholder-[#f4ecd8]/30 transition-colors"
                        />
                    </div>
                    <div>
                        <label className="block text-[10px] tracking-[0.28em] uppercase text-[#d4af37]/70 mb-2">
                            Description / note
                        </label>
                        <textarea
                            data-testid="contribution-note"
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            rows={5}
                            placeholder="Décrivez l'information transmise au cabinet…"
                            className="w-full bg-transparent border border-white/10 focus:border-[#d4af37]/40 outline-none px-4 py-3 text-[14px] leading-[1.85] font-serif text-[#f4ecd8] placeholder-[#f4ecd8]/30 transition-colors resize-y"
                        />
                    </div>
                    <div className="grid sm:grid-cols-2 gap-6">
                        <div>
                            <label className="block text-[10px] tracking-[0.28em] uppercase text-[#d4af37]/70 mb-2">
                                Date
                            </label>
                            <input
                                type="date"
                                data-testid="contribution-date"
                                value={date}
                                onChange={(e) => setDate(e.target.value)}
                                className="w-full bg-transparent border-b border-white/15 focus:border-[#d4af37] outline-none py-2.5 text-[14px] font-serif text-[#f4ecd8] transition-colors"
                            />
                        </div>
                        <div>
                            <label className="block text-[10px] tracking-[0.28em] uppercase text-[#d4af37]/70 mb-2">
                                Document à joindre (facultatif)
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                                <Paperclip strokeWidth={1.3} size={14} className="text-[#d4af37]/70" />
                                <span className="text-[13px] font-serif text-[#f4ecd8]/80 truncate">
                                    {file ? file.name : "Choisir un fichier (max 25 Mo)"}
                                </span>
                                <input
                                    type="file"
                                    data-testid="contribution-file"
                                    onChange={(e) => setFile(e.target.files?.[0] || null)}
                                    className="hidden"
                                />
                            </label>
                        </div>
                    </div>
                    <label className="flex items-start gap-3 cursor-pointer select-none">
                        <input
                            type="checkbox"
                            data-testid="contribution-confidential"
                            checked={confidential}
                            onChange={(e) => setConfidential(e.target.checked)}
                            className="mt-1 accent-[#d4af37]"
                        />
                        <div>
                            <div className="text-[12.5px] text-[#f4ecd8]/90 font-serif">
                                Visible uniquement par XV Capital Partners
                            </div>
                            <div className="mt-1 text-[11px] text-[#f4ecd8]/45 italic leading-snug">
                                Cette information ne sera consultable que par votre interlocuteur et ne sera pas réintégrée au journal partagé.
                            </div>
                        </div>
                    </label>

                    {err && (
                        <div className="text-[12px] text-[#e89090]/85 border-l-2 border-[#e89090]/50 pl-3 py-1 italic">
                            {err}
                        </div>
                    )}
                </div>

                <div className="px-7 sm:px-10 py-6 border-t border-white/[0.06] flex flex-wrap gap-3 justify-end">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-5 py-2.5 text-[10px] tracking-[0.28em] uppercase text-[#f4ecd8]/60 hover:text-[#f4ecd8] transition-colors"
                    >
                        Annuler
                    </button>
                    <button
                        type="button"
                        data-testid="contribution-submit"
                        disabled={busy}
                        onClick={submit}
                        className="px-6 py-2.5 text-[10px] tracking-[0.28em] uppercase border border-[#d4af37]/50 text-[#d4af37] hover:bg-[#d4af37] hover:text-[#070d18] transition-all duration-500 disabled:opacity-30"
                    >
                        {busy ? "Envoi…" : "Transmettre au cabinet"}
                    </button>
                </div>
            </div>
        </div>
    );
}


function SharedJournal({ dossier, contributions, reports, documents }) {
    // Build a merged timeline of events from multiple sources
    const events = [];

    // XV activity : validated step reports
    for (const s of dossier.timeline || []) {
        const r = s.report;
        if (r && (r.body || "").trim()) {
            events.push({
                kind: "xv-synthese",
                date: r.updated_at,
                title: r.title || `Synthèse — ${s.label}`,
                context: s.label,
                actor: "XV Capital Partners",
            });
        }
        if (s.state === "done" && s.completed_at) {
            events.push({
                kind: "xv-validation",
                date: s.completed_at,
                title: `${s.label} validée`,
                context: "Validation interne",
                actor: "XV Capital Partners",
            });
        }
    }

    // Standalone reports
    for (const r of reports || []) {
        if (r.validated_at) {
            events.push({
                kind: "xv-synthese",
                date: r.validated_at,
                title: r.title,
                context: "Synthèse partagée",
                actor: "XV Capital Partners",
            });
        }
    }

    // Client contributions
    for (const c of contributions || []) {
        events.push({
            kind: "client",
            date: c.date || c.created_at,
            title: c.title,
            context: c.confidential ? "Contribution confidentielle" : "Contribution client",
            actor: c.author_name || "Client",
        });
    }

    // Documents shared
    for (const d of documents || []) {
        if (d.uploaded_at) {
            events.push({
                kind: "doc",
                date: d.uploaded_at,
                title: d.filename,
                context: "Document partagé",
                actor: "XV Capital Partners",
            });
        }
    }

    events.sort((a, b) => (b.date || "").localeCompare(a.date || ""));
    if (events.length === 0) return null;

    return (
        <section
            data-testid="shared-journal"
            className="mb-16"
        >
            <div className="overline text-[#d4af37]/70 mb-3">
                Journal stratégique partagé
            </div>
            <h2
                className="font-serif text-[24px] mb-3"
                style={{ fontWeight: 400 }}
            >
                Échanges & validations
            </h2>
            <p className="text-[12.5px] text-[#f4ecd8]/45 mb-8 italic font-light leading-relaxed max-w-2xl">
                Une vue d'ensemble chronologique : activités du cabinet,
                contributions transmises de votre part, documents partagés et
                validations importantes.
            </p>
            <ul className="border-l border-white/[0.08] ml-2 space-y-7 pl-7">
                {events.map((e, i) => (
                    <li
                        key={i}
                        className="relative"
                        data-testid={`journal-event-${i}`}
                    >
                        <span
                            className={`absolute -left-[35px] top-1.5 w-2 h-2 rounded-full ${
                                e.kind === "client"
                                    ? "bg-[#f4ecd8]/60 ring-2 ring-[#f4ecd8]/20"
                                    : "bg-[#d4af37] ring-2 ring-[#d4af37]/30"
                            }`}
                        />
                        <div className="text-[10px] tracking-[0.28em] uppercase text-[#f4ecd8]/40 mb-1">
                            {e.date
                                ? new Date(e.date).toLocaleDateString("fr-FR", {
                                      day: "2-digit",
                                      month: "long",
                                      year: "numeric",
                                  })
                                : "—"}
                            <span className="ml-3 text-[#d4af37]/70">
                                {e.context}
                            </span>
                        </div>
                        <div
                            className="font-serif text-[16px] sm:text-[18px] text-[#f4ecd8]/90 leading-snug"
                            style={{ fontWeight: 400 }}
                        >
                            {e.title}
                        </div>
                        <div className="mt-1 text-[11px] text-[#f4ecd8]/45">
                            {e.actor}
                        </div>
                    </li>
                ))}
            </ul>
        </section>
    );
}


function RecentActivityBlock({ dossier }) {
    // Build an activity feed from the dossier itself :
    //   – Published reports (validated CRs)
    //   – Done steps without a CR (still counted as activity)
    // Sorted descending by date, capped to 4 lines.
    const events = [];
    for (const s of dossier.timeline || []) {
        const rep = s.report;
        if (rep && (rep.body || "").trim()) {
            events.push({
                kind: "report",
                date: rep.updated_at,
                label: `Synthèse confidentielle déposée — ${s.label}`,
            });
        } else if (s.state === "done" && s.completed_at) {
            events.push({
                kind: "step",
                date: s.completed_at,
                label: `${s.label} — étape finalisée`,
            });
        }
    }
    events.sort((a, b) => (b.date || "").localeCompare(a.date || ""));
    const top = events.slice(0, 4);
    if (top.length === 0) return null;

    return (
        <section
            data-testid="client-recent-activity"
            className="mb-16 bg-white/[0.02] border border-white/[0.06] p-10"
        >
            <div className="overline text-[#d4af37]/70 mb-3">Activité récente</div>
            <h2 className="font-serif text-[22px] mb-8" style={{ fontWeight: 400 }}>
                Dernières avancées
            </h2>
            <ul className="divide-y divide-white/[0.06]">
                {top.map((e, i) => (
                    <li
                        key={i}
                        className="grid grid-cols-[auto_1fr_auto] items-center gap-5 py-4"
                    >
                        <span
                            className={`w-1.5 h-1.5 rounded-full ${
                                e.kind === "report"
                                    ? "bg-[#d4af37]"
                                    : "bg-[#d4af37]/40"
                            }`}
                        />
                        <span className="font-serif text-[14.5px] sm:text-[15.5px] text-[#f4ecd8]/85 leading-snug">
                            {e.label}
                        </span>
                        <span className="text-[10px] tracking-[0.24em] uppercase text-[#f4ecd8]/45 whitespace-nowrap">
                            {e.date
                                ? new Date(e.date).toLocaleDateString("fr-FR", {
                                      day: "2-digit",
                                      month: "short",
                                      year: "2-digit",
                                  })
                                : "—"}
                        </span>
                    </li>
                ))}
            </ul>
        </section>
    );
}

// ─────────────────────────────────────────────────────────────
// PartnerInsightCard — sanitised view of partner-matching results.
// Exposes ONLY a count + average compatibility — never the names.
// ─────────────────────────────────────────────────────────────

function PartnerInsightCard({ insight }) {
    const generatedAt = insight.generated_at
        ? new Date(insight.generated_at).toLocaleDateString("fr-FR", {
              day: "2-digit",
              month: "long",
              year: "numeric",
          })
        : null;
    const count = insight.count || 0;
    const avg = insight.avg_compatibility || 0;
    const high = insight.high_band_count || 0;

    return (
        <section
            data-testid="client-partner-insight"
            className="mb-16 bg-white/[0.02] border border-white/[0.06] p-10 lg:p-12"
        >
            <div className="flex items-start justify-between flex-wrap gap-6 mb-8">
                <div>
                    <div className="overline text-[#d4af37]/70 mb-3">
                        <Network
                            strokeWidth={1}
                            size={14}
                            className="inline-block mr-2 -mt-0.5 text-[#d4af37]"
                        />
                        Réseau partenaires
                    </div>
                    <h2
                        className="font-serif text-[24px] sm:text-[28px] text-[#f4ecd8] leading-tight"
                        style={{ fontWeight: 400 }}
                    >
                        Cartographie d'affinités{" "}
                        <em className="not-italic text-[#d4af37]">
                            réalisée.
                        </em>
                    </h2>
                </div>
                {generatedAt && (
                    <div className="text-[10px] tracking-[0.28em] uppercase text-[#f4ecd8]/45">
                        Mise à jour · {generatedAt}
                    </div>
                )}
            </div>

            <p className="text-[14px] text-[#f4ecd8]/65 leading-[1.85] font-light mb-10 max-w-3xl">
                Le cabinet a effectué une lecture stratégique de son cercle
                privé en regard de votre dossier. Pour des raisons strictes
                de confidentialité réciproque, l'identité des partenaires
                concernés n'est pas affichée ici — elle est qualifiée
                directement par votre interlocuteur principal lorsqu'un
                rapprochement est jugé pertinent.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-px bg-white/[0.06] border border-white/[0.06]">
                <div
                    data-testid="partner-insight-count"
                    className="bg-[#070d18] p-7 sm:p-8"
                >
                    <div className="overline text-[#d4af37]/80 mb-5">
                        Partenaires compatibles
                    </div>
                    <div
                        className="font-serif text-[44px] sm:text-[54px] leading-none text-[#f4ecd8] mb-2"
                        style={{ fontWeight: 300 }}
                    >
                        {count}
                    </div>
                    <p className="text-[12px] text-[#f4ecd8]/45 tracking-wide">
                        Identifiés dans le cercle privé
                    </p>
                </div>
                <div
                    data-testid="partner-insight-avg"
                    className="bg-[#070d18] p-7 sm:p-8"
                >
                    <div className="overline text-[#d4af37]/80 mb-5">
                        Compatibilité moyenne
                    </div>
                    <div
                        className="font-serif text-[44px] sm:text-[54px] leading-none text-[#f4ecd8] mb-2"
                        style={{ fontWeight: 300 }}
                    >
                        {avg}
                        <span className="text-[#d4af37] text-[28px] ml-1">
                            %
                        </span>
                    </div>
                    <p className="text-[12px] text-[#f4ecd8]/45 tracking-wide">
                        Indice synthétique d'affinité
                    </p>
                </div>
                <div
                    data-testid="partner-insight-high"
                    className="bg-[#070d18] p-7 sm:p-8"
                >
                    <div className="overline text-[#d4af37]/80 mb-5">
                        Affinités fortes
                    </div>
                    <div
                        className="font-serif text-[44px] sm:text-[54px] leading-none text-[#d4af37] mb-2"
                        style={{ fontWeight: 300 }}
                    >
                        {high}
                    </div>
                    <p className="text-[12px] text-[#f4ecd8]/45 tracking-wide">
                        Pistes prioritaires à qualifier
                    </p>
                </div>
            </div>

            <p className="mt-8 text-[11.5px] text-[#f4ecd8]/40 italic font-light leading-relaxed max-w-3xl">
                Aucun engagement contractuel n'est associé à cette
                cartographie. Toute mise en relation est soumise à votre
                accord exprès, après échange préalable avec votre
                interlocuteur.
            </p>
        </section>
    );
}



// ─────────────────────────────────────────────────────────────
// ClientShareablesView — three curated cards from the cockpit:
// 1. Expertises mobilisées (no partner names)
// 2. Questions stratégiques à l'étude
// 3. Réflexions stratégiques XV (timestamped notes)
// Each card hides itself when empty, preserving the minimalist
// signature of the espace client.
// ─────────────────────────────────────────────────────────────

function ClientShareablesView({ dossier }) {
    const expertises = dossier.expertises || [];
    const questions = dossier.questions || [];
    const reflections = dossier.reflections || [];

    if (
        expertises.length === 0 &&
        questions.length === 0 &&
        reflections.length === 0
    ) {
        return null;
    }

    return (
        <section
            data-testid="client-shareables"
            className="mb-16 grid lg:grid-cols-12 gap-10"
        >
            {/* EXPERTISES MOBILISÉES */}
            {expertises.length > 0 && (
                <div
                    data-testid="client-expertises-card"
                    className="lg:col-span-5 bg-white/[0.02] border border-white/[0.06] p-10"
                >
                    <div className="overline text-[#d4af37]/70 mb-3">
                        <Briefcase
                            strokeWidth={1}
                            size={13}
                            className="inline-block mr-2 -mt-0.5 text-[#d4af37]"
                        />
                        Expertises mobilisées
                    </div>
                    <p className="text-[12.5px] text-[#f4ecd8]/45 italic font-light mb-7 leading-relaxed">
                        Les expertises mobilisées évoluent selon la nature et
                        les enjeux du dossier.
                    </p>
                    <ul className="space-y-2.5">
                        {expertises.map((opt) => (
                            <li
                                key={opt}
                                data-testid={`client-expertise-${opt}`}
                                className="flex items-center gap-3 py-1.5 text-[14px] font-serif text-[#f4ecd8]/90"
                            >
                                <span className="w-4 h-4 border border-[#d4af37]/70 bg-[#d4af37]/15 flex items-center justify-center shrink-0">
                                    <Check
                                        strokeWidth={1.8}
                                        size={10}
                                        className="text-[#d4af37]"
                                    />
                                </span>
                                {opt}
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {/* QUESTIONS STRATÉGIQUES À L'ÉTUDE */}
            {questions.length > 0 && (
                <div
                    data-testid="client-questions-card"
                    className={`bg-white/[0.02] border border-white/[0.06] p-10 ${
                        expertises.length > 0 ? "lg:col-span-7" : "lg:col-span-12"
                    }`}
                >
                    <div className="overline text-[#d4af37]/70 mb-3">
                        <HelpCircle
                            strokeWidth={1}
                            size={13}
                            className="inline-block mr-2 -mt-0.5 text-[#d4af37]"
                        />
                        Questions stratégiques à l'étude
                    </div>
                    <p className="text-[12.5px] text-[#f4ecd8]/45 italic font-light mb-7 leading-relaxed max-w-2xl">
                        Réflexions actuellement menées par le cabinet sur votre
                        dossier.
                    </p>
                    <ul className="space-y-3">
                        {questions.map((q, i) => (
                            <li
                                key={i}
                                data-testid={`client-question-${i}`}
                                className="flex items-start gap-4 py-2 border-b border-white/[0.05] last:border-b-0 text-[14.5px] font-serif text-[#f4ecd8]/85 leading-[1.75]"
                            >
                                <span className="text-[10px] tracking-[0.28em] uppercase text-[#d4af37]/55 mt-1.5 shrink-0">
                                    {String(i + 1).padStart(2, "0")}
                                </span>
                                <span className="flex-1">{q}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {/* RÉFLEXIONS STRATÉGIQUES XV */}
            {reflections.length > 0 && (
                <div
                    data-testid="client-reflections-card"
                    className="lg:col-span-12 bg-[#070d18] border border-[#d4af37]/15 p-10 lg:p-12"
                >
                    <div className="flex items-start justify-between flex-wrap gap-4 mb-3">
                        <div>
                            <div className="overline text-[#d4af37]/70 mb-3">
                                <Quote
                                    strokeWidth={1}
                                    size={13}
                                    className="inline-block mr-2 -mt-0.5 text-[#d4af37]"
                                />
                                Réflexions stratégiques XV
                            </div>
                            <h2
                                className="font-serif text-[22px] sm:text-[24px] text-[#f4ecd8] leading-tight"
                                style={{ fontWeight: 400 }}
                            >
                                Points de lecture du cabinet
                            </h2>
                        </div>
                        <div className="text-[10px] tracking-[0.32em] uppercase text-[#d4af37]/55">
                            {reflections.length} note
                            {reflections.length > 1 ? "s" : ""}
                        </div>
                    </div>
                    <p className="text-[12.5px] text-[#f4ecd8]/45 italic font-light mb-10 leading-relaxed max-w-3xl">
                        Notes stratégiques rédigées par XV Capital Partners
                        dans le cadre de l'analyse de votre dossier.
                    </p>

                    <ul className="space-y-8 max-w-3xl">
                        {reflections.map((r) => (
                            <li
                                key={r.id}
                                data-testid={`client-reflection-${r.id}`}
                                className="border-l border-[#d4af37]/35 pl-6"
                            >
                                <p className="font-serif text-[16px] sm:text-[17px] leading-[1.9] text-[#f4ecd8]/90 italic">
                                    « {r.body} »
                                </p>
                                <div className="mt-4 flex items-center justify-between text-[10px] tracking-[0.32em] uppercase">
                                    <span className="text-[#f4ecd8]/45">
                                        {r.created_at
                                            ? new Date(
                                                  r.created_at,
                                              ).toLocaleDateString("fr-FR", {
                                                  day: "2-digit",
                                                  month: "long",
                                                  year: "numeric",
                                              })
                                            : ""}
                                    </span>
                                    <span className="text-[#d4af37]/75">
                                        XV Capital Partners
                                    </span>
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </section>
    );
}
